import { uuidv4 } from 'uuid/v4';

import * as Actions from './Commands/DeviceActions';
import { MetaDataUpdate } from './MetaData/MetaDataActions';
import { warning, critical } from '../Notifications/notice';
import { endpointRequest } from '../EndpointService/EndpointFunctions';

export default function createDevicesService(jokiInstance) {
    const serviceId = 'DevicesService';
    const devices = new Map();
    const joki = jokiInstance;

    const requestsInProgress = {};

    let autoUpdateInterval = null;

    const updateSpeeds = {
        stop: 10000000,
        slow: 180000,
        normal: 120000,
        fast: 90000,
        previous: 90000,
    };
    let updateIntervalSpeed = updateSpeeds.slow;
    updateSpeeds.previous = updateIntervalSpeed;

    function messageHandler(event) {
        // console.log("DevicesService:messageHandler", event);
        switch (event.key) {
            case 'getDevices':
            case 'getState':
                return get();
            case 'getDevice':
                return get(event.body.code, false);
            case 'forceDeviceUpdate':
                return get(event.body.code, true);
            case 'setDevice':
                set(event.body);
                break;
            case 'updateCoordinates':
                break;
            case 'login':
                break;
            case 'logout':
                devices.clear();
                autoUpdateInterval !== null && clearInterval(autoUpdateInterval);
                break;
            case 'DataResetRequired':
                resetData();
                break;
            case 'initialize':
                init();
                break;
            case 'updateMeta':
                updateMetaData(event.body.device, event.body.metaData);
                //TODO: Sending meta update command to the backend
                break;
            case 'commandsForDevice':
                return Actions.deviceTypeCommands(event.body);
            case 'executeDeviceCommand':
                executeCommand(event.body.command, event.body.device, event.body.params);
                break;
            case 'loadCodes':
                return fetchDeviceCodes(true);
            case 'getCurrentDevices':
                return new Map(devices);
            case 'updateIntervalSpeed':
                frequentUpdate(event.body);
                break;
            case 'getDeviceInventoryList':
                return fetchDeviceInventoryList();
            default:
                break;
        }
    }

    /**
     * Gets all devices of a single device information if deviceCode passed
     * @param {String} deviceCode The DeviceCode of the device if the information fo a specific device is needed 
     * @param {Boolean} forceUpdate If the data must be requested from API again and not coming from cache
     * @returns 
     */
    function get(deviceCode = null, forceUpdate = false) {
        if (deviceCode === null) {
            // Fetch a simplified list
            getSimplifiedList();
            if (autoUpdateInterval === null) frequentUpdate();
            return Array.from(devices.values());
        }
        if (devices.has(deviceCode)) {
            // Fetch Details from server and update info when it comes
            // But first return the current set
            fetchDetailedDataForDevice(deviceCode, forceUpdate);
            return devices.get(deviceCode);
        }
        fetchDetailedDataForDevice(deviceCode, forceUpdate);
        // Get all devices from the backend
        // fetchDeviceCodes(true);

        // const errMsg = `Unknown device ${deviceCode}`;
        // throw errMsg;
    }

    function set(device) {
        // TODO: check where is this used
        if (device._code === undefined) {
            device._code = uuidv4();
        }

        devices.set(device._code, device);

        this.joki.trigger({
            from: serviceId,
            key: 'deviceDataSet', // TODO: check where this key is used
            body: device,
        });
    }

    function init() {
        //fetchDeviceCodes();
        getSimplifiedList();
    }


    function resetData() {
        devices.clear();
        triggerUpdatedEvent();
        get();
    }

    function executeCommand(command, device, params = null) {
        // TODO: check how we send commands
        if (Actions.commandExecutors[command] === undefined) {
            warning(`Device command '${command}' is unknown.`);
        } else {
            Actions.commandExecutors[command](device, params);
        }
    }

    function updateMetaData(device = undefined, metaData = undefined) {
        if (device !== undefined && metaData !== undefined) {
            MetaDataUpdate(device, metaData)
                .then(res => {
                    fetchDetailedDataForDevice(device.code, true);
                })
                .catch(err => {
                    throw err;
                });
        }
    }

    async function fetchDeviceCodes(triggerUpdate = false) {
        try {
            const results = await endpointRequest(joki, 'deviceList_codes');

            mergeResultsToCurrent(results.devices);
            if (triggerUpdate) triggerUpdatedEvent();
        } catch (err) {
            if (err.name !== 'AbortError' && err.name !== 'EndpointQueueError') {
                console.error(err);
            }
            //throw new Error(`DevicesService:fetchDeviceCodes:ERROR: Initialzing device Codes failed: \n ${err}`);
        }
    }

    async function fetchDeviceInventoryList(triggerUpdate = false) {
        try {
            const results = await endpointRequest(joki, 'device_inventory_list',{filters:{retired:true}});
            return results;

        } catch (err) {
            if (err.name !== 'AbortError' && err.name !== 'EndpointQueueError') {
                console.error(err);
            }
            return undefined;
        }
    }
    /**
     * Gets the default device list
     */
    async function getSimplifiedList() {
        try {
            const results = await endpointRequest(joki, 'deviceList_default', {
                filters: {
                    notProvisioned: true,
                },
            });

            mergeResultsToCurrent(results.devices);
            triggerUpdatedEvent();
        } catch (err) {
            if (err.name !== 'AbortError' && err.name !== 'EndpointQueueError') {
                console.error('DevicesService:getSimplifiedList:', err);
            }

            // throw err;
        }
    }



    async function fetchDetailedDataForDevice(deviceCode, forceUpdate = false) {
        const endpointId = forceUpdate === true ? 'device_details_forced' : 'device_details';
        if (requestsInProgress[endpointId] === deviceCode && forceUpdate === false) return;
        try {
            requestsInProgress[endpointId] = deviceCode;
            const results = await endpointRequest(joki, endpointId, {
                filters: {
                    code: `"${deviceCode}"`,
                },
            });

            if (results.device) {
                mergeResultsToCurrent([results.device]);
                triggerDeviceUpdatedEvent(results.device.code);
                triggerUpdatedEvent();
            } else {
                console.warn('DevicesService:getDetailedDataForDevice: Results do not contain a device', results);
            }

            requestsInProgress.deviceList_default = false;
        } catch (err) {
            if (err.name !== 'EndpointQueueError' && err.name !== 'AbortError') {
                console.error('DevicesService:fetchDetailedDataForDevice: Catch!', err, err.name);
                //throw err;
            } else {
                console.warn(`DevicesService:fetchDetailedDataForDevice: Could not fetch Device details for ${deviceCode}!`, err);
            }
        }
    }

    function frequentUpdate(newIntervalSpeed = null) {
        if (autoUpdateInterval !== null) {
            clearInterval(autoUpdateInterval);
        }

        if (typeof newIntervalSpeed === 'number') {
            updateSpeeds.previous = updateIntervalSpeed;
            updateIntervalSpeed = newIntervalSpeed;
        }

        // Stop the intarval
        if (typeof newIntervalSpeed === 'string' && newIntervalSpeed === 'stop') {
            // console.log('Stop interval');
            clearInterval(autoUpdateInterval);
            return;
        }

        if (typeof newIntervalSpeed === 'string' && typeof updateSpeeds[newIntervalSpeed] === 'number') {
            if (updateSpeeds[newIntervalSpeed] !== updateIntervalSpeed) {
                // console.log(`Update interval speed to ${newIntervalSpeed} (${updateSpeeds[newIntervalSpeed]})`);
                updateSpeeds.previous = updateIntervalSpeed;
                updateIntervalSpeed = updateSpeeds[newIntervalSpeed];
            }
        }

        // console.log(`Update interval speed is ${updateIntervalSpeed}`);

        autoUpdateInterval = setInterval(async () => {
            try {
                const results = await endpointRequest(joki, 'device_frequent_update');
                // console.log('Auto Update Interval', results);

                const devicesWithAlertsChanged = results.devices.filter(device => {
                    const deviceCode = device.code;
                    const oldDevice = devices.get(deviceCode);
                    if (!oldDevice) return true;

                    if (oldDevice.alertCount.all !== device.alertCount.all) return true;
                    if (oldDevice.alertCount.active.total !== device.alertCount.active.total) return true;
                    return false;
                });

                if (devicesWithAlertsChanged.length > 0) {
                    const devicesWithAlarts = devicesWithAlertsChanged.reduce((acc, cur) => {
                        if (cur.alertCount.active.total > 0) {
                            const device = devices.get(cur.code);
                            acc.push(device.name || device.code);
                        }
                        return acc;
                    }, []);

                    devicesWithAlarts.length === 1 && critical(`Alert for device ${devicesWithAlarts.join(', ')} has changed`);
                    devicesWithAlarts.length > 1 && critical(`Alerts for devices ${devicesWithAlarts.join(', ')} have changed`);
                    mergeResultsToCurrent(results.devices);
                    triggerUpdatedEvent();

                    joki.broadcast({
                        from: serviceId,
                        key: 'DevicesWithAlertChanges',
                        body: devicesWithAlertsChanged,
                    });
                } else {
                    mergeResultsToCurrent(results.devices);
                }
            } catch (err) {
                clearInterval(autoUpdateInterval);
            }
        }, updateIntervalSpeed);
    }

    function mergeResultsToCurrent(updatedDevices) {
        if (Array.isArray(updatedDevices)) {
            updatedDevices.forEach(device => {
                if (device) {
                    // if (device.lifeCycleStatus) {
                    //     if (device.lifeCycleStatus !== 'REGISTERED') {
                    if (devices.has(device.code)) {
                        devices.set(device.code, Object.assign({}, devices.get(device.code), device));
                    } else {
                        devices.set(device.code, device);
                    }
                    // }
                    // } else { // this one is for fixing the tests
                    //     if (devices.has(device.code)) {
                    //         devices.set(device.code, Object.assign({}, devices.get(device.code), device));
                    //     } else {
                    //         devices.set(device.code, device);
                    //     }
                    // }
                }
            });
        }
    }

    function triggerUpdatedEvent() {
        joki.trigger({
            from: serviceId,

            serviceUpdate: true,
            body: Array.from(devices.values()),
        });
    }

    function triggerDeviceUpdatedEvent(deviceCode) {
        joki.trigger({
            from: serviceId,
            key: 'deviceDataUpdated',
            body: devices.get(deviceCode),
        });
    }

    joki.addService({ id: serviceId, fn: messageHandler });

    return {
        get,
        set,
        init,
    };
}
